import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Components
import MetalsTicker from "@/components/MetalsTicker";
import FinancialTicker from "@/components/FinancialTicker";

// Pages
import LandingPage from "@/pages/LandingPage";
import WorkInProgressPage from "@/pages/WorkInProgressPage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import ProfilePage from "@/pages/ProfilePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import NotificationsPage from "@/pages/NotificationsPage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import AdminDashboard from "@/pages/AdminDashboard";
import DisclosuresPage from "@/pages/DisclosuresPage";
import GoldPurchaseRecommendationPage from "@/pages/GoldPurchaseRecommendationPage";
import GoldSalesRecommendationPage from "@/pages/GoldSalesRecommendationPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getLoginUrl = () => {
  // Use the current origin for the login page - works in every environment
  // (localhost, preview, production, custom domain) without any hardcoded URLs.
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/login`;
  }
  return "/login";
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  useEffect(() => {
    // CRITICAL (Emergent Auth playbook): If returning from OAuth callback,
    // skip the /auth/me check. AuthCallback will exchange the session_id and
    // establish the session first. Running checkAuth here races with AuthCallback
    // and causes a redirect loop on production/custom domains.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback - Process session_id from Emergent OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      // Check for referral code in localStorage
      const referralCode = localStorage.getItem("referral_code");

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { 
            session_id: sessionId,
            referral_code: referralCode || undefined
          },
          { withCredentials: true }
        );

        // Clear referral code after use
        if (referralCode) {
          localStorage.removeItem("referral_code");
        }

        if (response.data.user) {
          setUser(response.data.user);
          toast.success("Welcome to GeoCore Global!");
          
          // Get the intended redirect URL from localStorage (set before OAuth redirect)
          const savedRedirect = localStorage.getItem("auth_redirect");
          localStorage.removeItem("auth_redirect"); // Clean up
          
          // Redirect to saved destination or default to subscription/manage access
          const redirectTo = savedRedirect || "/subscription";
          navigate(redirectTo, { replace: true, state: { user: response.data.user } });
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      // For split deployment: redirect to external auth URL if configured
      const loginUrl = getLoginUrl();
      if (loginUrl.startsWith("http")) {
        window.location.href = loginUrl;
      } else {
        navigate("/login", { state: { from: location } });
      }
    }
    if (!loading && user && requireAdmin && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/subscription");
    }
  }, [user, loading, requireAdmin, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;
  if (requireAdmin && user.role !== "admin") return null;

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (from Emergent OAuth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/disclosures" element={<DisclosuresPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Protected Routes */}
      <Route path="/profile" element={
        <ProtectedRoute><ProfilePage /></ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute><SubscriptionPage /></ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute><NotificationsPage /></ProtectedRoute>
      } />
      <Route path="/gold-purchase-recommendation" element={
        <ProtectedRoute><GoldPurchaseRecommendationPage /></ProtectedRoute>
      } />
      <Route path="/gold-sales-recommendation" element={
        <ProtectedRoute><GoldSalesRecommendationPage /></ProtectedRoute>
      } />
      <Route path="/payment-methods" element={
        <ProtectedRoute><PaymentMethodsPage /></ProtectedRoute>
      } />

      {/* Legacy dashboard route — redirects to the new default landing page */}
      <Route path="/dashboard" element={<Navigate to="/subscription" replace />} />
      <Route path="/investments" element={<Navigate to="/subscription" replace />} />
      <Route path="/investments/:id" element={<Navigate to="/subscription" replace />} />
      <Route path="/portfolio" element={<Navigate to="/gold-sales-recommendation" replace />} />
      <Route path="/investing-parameters" element={<Navigate to="/subscription" replace />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>
      } />
      
      {/* Referral Route */}
      <Route path="/r/:code" element={<ReferralRedirect />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Referral Redirect Component
const ReferralRedirect = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Store referral code in localStorage and redirect to login
    if (code) {
      localStorage.setItem("referral_code", code.toUpperCase());
      toast.info("Referral code applied! Sign up to continue.");
    }
    // For split deployment: redirect to external auth URL if configured
    const loginUrl = getLoginUrl();
    if (loginUrl.startsWith("http")) {
      window.location.href = loginUrl;
    } else {
      navigate("/login", { replace: true });
    }
  }, [code, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p>Redirecting...</p>
    </div>
  );
};

const SITE_PAUSED = true;

function App() {
  if (SITE_PAUSED) return <WorkInProgressPage />;
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <FinancialTicker />
          <div className="pt-10 pb-16"> {/* Add padding for both tickers */}
            <AppRouter />
          </div>
          <MetalsTicker />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
